import React, { useState, useRef, useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import { RootState } from "configureStore";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { useTranslation } from "react-i18next";
import { CalendarDays } from "lucide-react";
import useIsPhone from "features/files/use-is-phone";
import { PostEvent, PostEventInput } from "types/Post";
import { EMPTY_EVENT_FORM, EventFormValues, eventFormFromEvent, eventFormToInput } from "../utils/eventForm";
import { EventFields } from "./EventFields";

/** What the form hands over when the post is published or saved. */
export interface PostFormSubmission {
  content: string;
  /** The long text behind «Rodyti daugiau»; null when the section is closed or empty. */
  details: string | null;
  isImportant: boolean;
  files: File[];
  removedFileIds: string[];
  isGroupPost: boolean;
  event?: PostEventInput;
}

interface PostFormProps {
  onSubmit: (submission: PostFormSubmission) => Promise<void>;
  isSubmitting?: boolean;
  initialContent?: string;
  /** The long text of the post being edited; the details editor opens when it is there. */
  initialDetails?: string | null;
  initialIsImportant?: boolean;
  postId?: string;
  onCancel?: () => void;
  submitButtonText?: string;
  existingFiles?: Array<{ id: string; title?: string; name?: string }>;
  getFileUrl?: (fileId: string) => Promise<string>;
  initialIsGroupPost?: boolean;
  /** The group box on a plain post. */
  showGroupPostCheckbox?: boolean;
  /** The group box on an event post: an organiser with group rights may announce an event without being allowed plain group posts. */
  showGroupEventCheckbox?: boolean;
  /** Only an organiser makes or changes an event: a new post may become one, an event post shows its fields. */
  canHaveEvent?: boolean;
  /** The event of the post being edited. */
  initialEvent?: PostEvent | null;
  /** Turns the event fields on, also in a form that is open already; the text typed so far stays. */
  startAsEvent?: boolean;
}

// Quill answers "<p><br></p>" for an editor with nothing in it.
const isContentEmpty = (html: string) => {
  if (!html) return true;
  const text = html
    .replace(/<br\s*\/?>/gi, " ")
    .replace(/&nbsp;/gi, " ")
    .replace(/<[^>]*>/g, "")
    .trim();
  return text.length === 0;
};

// On a phone the editor text is 16 px (iOS Safari zooms into anything smaller), the toolbar buttons are
// finger-sized and the link box takes the editor's width instead of being cut off by the frame.
const editorFrameClassName =
  "border border-gray-300 rounded-lg overflow-hidden focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-transparent " +
  "max-md:[&_.ql-container]:text-base max-md:[&_.ql-toolbar_button]:!h-9 max-md:[&_.ql-toolbar_button]:!w-9 " +
  "max-md:[&_.ql-tooltip]:!left-0 max-md:[&_.ql-tooltip]:!right-0 max-md:[&_.ql-tooltip]:!whitespace-normal";

/** The red cross on a photo to be posted; always shown for a finger, only on hover for a mouse. */
function RemovePhotoButton({ onClick }: { onClick: () => void }) {
  const { t } = useTranslation();
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={t("feed_delete")}
      // The ::after widens the hit area to 44 px without making the red circle bigger.
      className="absolute right-2 top-2 flex h-6 w-6 items-center justify-center rounded-full bg-red-500 text-white transition-opacity after:absolute after:-inset-2.5 after:content-[''] focus-visible:opacity-100 [@media(hover:hover)]:opacity-0 [@media(hover:hover)]:group-hover:opacity-100"
    >
      ×
    </button>
  );
}

export function PostForm({ 
  onSubmit, 
  isSubmitting = false,
  initialContent = "",
  initialDetails = null,
  initialIsImportant = false,
  postId,
  onCancel,
  submitButtonText,
  existingFiles = [],
  getFileUrl,
  initialIsGroupPost = false,
  showGroupPostCheckbox = false,
  showGroupEventCheckbox = false,
  canHaveEvent = false,
  initialEvent = null,
  startAsEvent = false
}: PostFormProps) {
 
  const { t } = useTranslation();
  const isPhone = useIsPhone();
  const { session } = useSelector((state: RootState) => state.authReducer);
  const [content, setContent] = useState(initialContent);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [previews, setPreviews] = useState<Map<string, string>>(new Map());
  const [existingFileUrls, setExistingFileUrls] = useState<{ [key: string]: string }>({});
  const [removedFileIds, setRemovedFileIds] = useState<Set<string>>(new Set());
  const [fileError, setFileError] = useState<string>("");
  const [loadingExistingFiles, setLoadingExistingFiles] = useState(false);
  const [isEditing, setIsEditing] = useState(!!postId);
  const [isGroupPost, setIsGroupPost] = useState(initialIsGroupPost);
  const [isImportant, setIsImportant] = useState(initialIsImportant);
  const [details, setDetails] = useState(initialDetails ?? "");
  const [isDetailsShown, setIsDetailsShown] = useState(!isContentEmpty(initialDetails ?? ""));
  // null = the post is not an event. The form is mounted fresh for each post being edited.
  const [eventForm, setEventForm] = useState<EventFormValues | null>(
    canHaveEvent && initialEvent ? eventFormFromEvent(initialEvent) : null
  );
  useEffect(() => {
    if (canHaveEvent && startAsEvent) setEventForm((current) => current ?? EMPTY_EVENT_FORM);
  }, [canHaveEvent, startAsEvent]);
  const eventInput = eventForm ? eventFormToInput(eventForm) : null;
  const isEventIncomplete = eventForm !== null && eventInput === null;
  const isGroupCheckboxShown = eventForm !== null ? showGroupEventCheckbox : showGroupPostCheckbox;
  const fileInputRef = useRef<HTMLInputElement>(null);
  const loadedPostIdRef = useRef<string | undefined>(undefined);
  
  // Helper to generate unique key for a file
  const getFileKey = (file: File): string => {
    return `${file.name}-${file.size}-${file.lastModified}`;
  };

  // Create a stable reference for existingFiles based on their IDs
  const existingFileIds = useMemo(
    () => existingFiles.map((f) => f.id).join(","),
    [existingFiles]
  );

  useEffect(() => {
    if (postId && !isEditing) {
      setContent(initialContent);
      setIsGroupPost(initialIsGroupPost);
      setIsEditing(true);
    } else if (!postId && isEditing) {
      setContent("");
      setSelectedFiles([]);
      setPreviews(new Map());
      setExistingFileUrls({});
      setRemovedFileIds(new Set());
      setIsEditing(false);
      setIsGroupPost(false);
      loadedPostIdRef.current = undefined;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [postId]);

  useEffect(() => {
    // Prevent loading if already loaded for this postId
    if (loadedPostIdRef.current === postId && postId) {
      return;
    }

    if (!postId || !existingFiles || existingFiles.length === 0 || !getFileUrl) {
      if (loadedPostIdRef.current !== undefined) {
      setLoadingExistingFiles(false);
      // Cleanup existing blob URLs before clearing
      setExistingFileUrls((currentUrls) => {
        Object.values(currentUrls).forEach((url) => {
          if (url.startsWith("blob:")) {
            URL.revokeObjectURL(url);
          }
        });
        return {};
      });
        loadedPostIdRef.current = undefined;
      }
      return;
    }

    loadedPostIdRef.current = postId;
    setLoadingExistingFiles(true);
    const urls: { [key: string]: string } = {};

    const loadExistingFiles = async () => {
      try {
        await Promise.all(
          existingFiles.map(async (file) => {
            try {
              const url = await getFileUrl(file.id);
              urls[file.id] = url;
            } catch (error) {
              // Silently handle error
            }
          })
        );
        setExistingFileUrls(urls);
      } catch (error) {
        // Silently handle error
      } finally {
        setLoadingExistingFiles(false);
      }
    };

    loadExistingFiles();

    // Cleanup blob URLs on unmount
    return () => {
      setExistingFileUrls((currentUrls) => {
        Object.values(currentUrls).forEach((url) => {
          if (url.startsWith("blob:")) {
            URL.revokeObjectURL(url);
          }
        });
        return {};
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [postId, existingFileIds]);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    setFileError(""); // Clear previous errors

    // Only allow image files
    const imageFiles = files.filter(file => file.type.startsWith("image/"));

    if (imageFiles.length !== files.length) {
      const rejectedCount = files.length - imageFiles.length;
      setFileError(t("feed_only_images_allowed", { count: rejectedCount }));
      return;
    }

    // Check total file count (max 10 files)
    const MAX_FILES = 10;
    const totalFiles = selectedFiles.length + imageFiles.length + (existingFiles.length - removedFileIds.size);
    if (totalFiles > MAX_FILES) {
      setFileError(t("feed_max_files_exceeded", { max: MAX_FILES }));
      return;
    }

    const newFiles = [...selectedFiles, ...imageFiles];
    setSelectedFiles(newFiles);

    // Load previews for each file and store them with file key
    imageFiles.forEach((file) => {
      if (file.type.startsWith("image/")) {
        const fileKey = getFileKey(file);
        const reader = new FileReader();
        reader.onload = (event) => {
          const result = event.target?.result as string;
          setPreviews((prev) => {
            const newMap = new Map(prev);
            newMap.set(fileKey, result);
            return newMap;
          });
        };
        reader.readAsDataURL(file);
      }
    });
  };

  const removeFile = (index: number) => {
    setSelectedFiles((prev) => {
      const fileToRemove = prev[index];
      if (fileToRemove) {
        // Remove preview using file key
        const fileKey = getFileKey(fileToRemove);
        setPreviews((prevPreviews) => {
          const newMap = new Map(prevPreviews);
          newMap.delete(fileKey);
          return newMap;
        });
      }
      return prev.filter((_, i) => i !== index);
    });
    setFileError(""); // Clear error when file is removed
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const removeExistingFile = (fileId: string) => {
    setRemovedFileIds((prev) => {
      const newSet = new Set(prev);
      newSet.add(fileId);
      return newSet;
    });
    setExistingFileUrls((prev) => {
      const newUrls = { ...prev };
      const urlToRemove = newUrls[fileId];
      // Revoke blob URL before removing from state
      if (urlToRemove && urlToRemove.startsWith("blob:")) {
        URL.revokeObjectURL(urlToRemove);
      }
      delete newUrls[fileId];
      return newUrls;
    });
  };

  const removeDetails = () => {
    setDetails("");
    setIsDetailsShown(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isContentEmpty(content) && selectedFiles.length === 0 && Object.keys(existingFileUrls).length === 0) return;
    if (isSubmitting || isEventIncomplete) return;

    try {
      await onSubmit({
        content,
        details: isDetailsShown && !isContentEmpty(details) ? details : null,
        isImportant,
        files: selectedFiles,
        removedFileIds: Array.from(removedFileIds),
        isGroupPost,
        event: eventInput ?? undefined,
      });
      setContent("");
      removeDetails();
      setIsImportant(false);
      setSelectedFiles([]);
      setPreviews(new Map());
      setIsGroupPost(false);
      setEventForm(null);
      // Cleanup blob URLs before clearing
      setExistingFileUrls((currentUrls) => {
        Object.values(currentUrls).forEach((url) => {
          if (url.startsWith("blob:")) {
            URL.revokeObjectURL(url);
          }
        });
        return {};
      });
      setRemovedFileIds(new Set());
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    } catch (error) {
      // Handle error silently
    }
  };

  const handleEditorChange = (value: string) => {
    setContent(value);
  };

  const userName = session?.name || session?.email || "User";

  // Strike-through and «clear formatting» are left out on a phone, where they would push the toolbar onto a second line.
  const toolbarOptions = isPhone
    ? [["bold", "italic", "underline"], [{ list: "ordered" }, { list: "bullet" }], ["link"]]
    : [
        ["bold", "italic", "underline", "strike"],
        [{ list: "ordered" }, { list: "bullet" }],
        ["link"],
        ["clean"],
      ];

  return (
    <div
      className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6"
      data-testid="post-form"
    >
      <form onSubmit={handleSubmit}>
        <div className="flex items-start space-x-3 max-sm:space-x-0">
          <div className="hidden flex-shrink-0 sm:block">
            <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white font-semibold text-sm">
              {userName.charAt(0).toUpperCase()}
            </div>
          </div>
          <div className="flex-1">
            {eventForm && (
              <EventFields values={eventForm} onChange={setEventForm} disabled={isSubmitting} focusTitle={!postId} />
            )}
            <div className={editorFrameClassName}>
              <ReactQuill
                value={content}
                onChange={handleEditorChange}
                placeholder={t("feed_whats_on_mind")}
                readOnly={isSubmitting}
                modules={{
                  toolbar: toolbarOptions,
                }}
                theme="snow"
              />
            </div>

            {isDetailsShown ? (
              <div className="mt-3">
                <div className={editorFrameClassName}>
                  <ReactQuill
                    value={details}
                    onChange={setDetails}
                    placeholder={t("news_details_placeholder")}
                    readOnly={isSubmitting}
                    modules={{
                      toolbar: toolbarOptions,
                    }}
                    theme="snow"
                  />
                </div>
                <button
                  type="button"
                  onClick={removeDetails}
                  disabled={isSubmitting}
                  className="mt-1 text-xs text-gray-500 hover:text-red-600 hover:underline max-md:py-2"
                >
                  {t("news_details_remove")}
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => setIsDetailsShown(true)}
                disabled={isSubmitting}
                className="mt-2 text-sm text-blue-600 hover:underline max-md:py-2"
              >
                {t("news_details_add")}
              </button>
            )}

            {existingFiles && existingFiles.length > 0 && (
              <div className="mt-3">
                {loadingExistingFiles ? (
                  <div className="grid grid-cols-2 gap-2">
                    {[1, 2].map((i) => (
                      <div key={i} className="h-32 bg-gray-200 rounded-lg animate-pulse"></div>
                    ))}
                  </div>
                ) : (
                  <div className="grid grid-cols-2 gap-2">
                    {existingFiles
                      .filter((file) => !removedFileIds.has(file.id))
                      .map((file) => (
                        <div key={file.id} className="relative group">
                          {existingFileUrls[file.id] ? (
                            <img
                              src={existingFileUrls[file.id]}
                              alt={file.title || file.name || `File ${file.id}`}
                              className="w-full h-32 object-cover rounded-lg"
                            />
                          ) : (
                            <div className="w-full h-32 bg-gray-200 rounded-lg flex items-center justify-center">
                              <span className="text-gray-400 text-sm">{t("feed_loading")}</span>
                            </div>
                          )}
                          <RemovePhotoButton onClick={() => removeExistingFile(file.id)} />
                        </div>
                      ))}
                  </div>
                )}
              </div>
            )}

            {selectedFiles.length > 0 && (
              <div className="mt-3 grid grid-cols-2 gap-2">
                {selectedFiles.map((file, index) => {
                  const fileKey = getFileKey(file);
                  const preview = previews.get(fileKey);
                  return (
                    <div key={fileKey} className="relative group">
                      {preview ? (
                        <img
                          src={preview}
                          alt={file.name || `Preview ${index + 1}`}
                          className="w-full h-32 object-cover rounded-lg"
                        />
                      ) : (
                        <div className="w-full h-32 bg-gray-200 rounded-lg flex items-center justify-center">
                          <span className="text-gray-400 text-sm">{t("feed_loading")}</span>
                        </div>
                      )}
                      <RemovePhotoButton onClick={() => removeFile(index)} />
                    </div>
                  );
                })}
              </div>
            )}

            {fileError && (
              <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm text-red-600">{fileError}</p>
              </div>
            )}

            <div className="flex flex-wrap items-center justify-between gap-2 mt-3 pt-3 border-t border-gray-200">
              <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="flex items-center space-x-2 px-3 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors max-md:min-h-11"
                  disabled={isSubmitting}
                >
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                    />
                  </svg>
                  <span className="text-sm font-medium">{t("feed_photo")}</span>
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  accept="image/*"
                  onChange={handleFileSelect}
                  className="hidden"
                  data-testid="photo-input"
                  disabled={isSubmitting}
                />
                {canHaveEvent && !postId && (
                  <button
                    type="button"
                    aria-pressed={eventForm !== null}
                    onClick={() => {
                      // Without the right to plain group posts, the group box goes away with the event.
                      if (eventForm && !showGroupPostCheckbox) setIsGroupPost(false);
                      setEventForm(eventForm ? null : EMPTY_EVENT_FORM);
                    }}
                    disabled={isSubmitting}
                    className={`flex items-center space-x-2 px-3 py-2 rounded-lg transition-colors max-md:min-h-11 ${
                      eventForm ? "text-blue-600 bg-blue-50" : "text-gray-600 hover:bg-gray-100"
                    }`}
                  >
                    <CalendarDays className="w-5 h-5" />
                    <span className="text-sm font-medium">{t("news_event_composer_toggle")}</span>
                  </button>
                )}
                {isGroupCheckboxShown ? (
                  <label className="flex items-center space-x-2 cursor-pointer max-md:min-h-11">
                    <input
                      type="checkbox"
                      checked={isGroupPost}
                      onChange={(e) => setIsGroupPost(e.target.checked)}
                      disabled={isSubmitting}
                      className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700 font-medium">{t("feed_group_post") || "Group Post"}</span>
                  </label>
                ) : null}
                <label className="flex items-center space-x-2 cursor-pointer max-md:min-h-11">
                  <input
                    type="checkbox"
                    checked={isImportant}
                    onChange={(e) => setIsImportant(e.target.checked)}
                    disabled={isSubmitting}
                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <span className="text-sm text-gray-700 font-medium">{t("news_important_checkbox")}</span>
                </label>
              </div>
              <div className="flex items-center space-x-2 max-md:w-full">
                {onCancel && (
                  <button
                    type="button"
                    onClick={onCancel}
                    disabled={isSubmitting}
                    className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-colors max-md:min-h-11 max-md:flex-1"
                  >
                    {t("feed_cancel")}
                  </button>
                )}
                <button
                  type="submit"
                  disabled={
                    (isContentEmpty(content) && selectedFiles.length === 0 && Object.keys(existingFileUrls).length === 0) ||
                    isSubmitting ||
                    isEventIncomplete
                  }
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center space-x-2 max-md:min-h-11 max-md:flex-1 max-md:justify-center"
                >
                  {isSubmitting ? (
                    <>
                      <svg
                        className="animate-spin h-4 w-4"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        ></path>
                      </svg>
                      <span>{postId ? t("feed_saving") : t("feed_posting")}</span>
                    </>
                  ) : (
                    <span>{submitButtonText || t("feed_post")}</span>
                  )}
                </button>
              </div>
             
            </div>
            <p className="mt-1 text-xs text-gray-500">
               <span className="text-red-500">*</span>{t("feed_only_images_allowed")}
            </p>
          </div>
        </div>
      </form>
    </div>
  );
}
